import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Message } from '../../contexts/ConversationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { MessageBubble } from './MessageBubble';
import { MessageRow } from './MessageRow';
import { PhaseTrailBlock } from './PhaseTrailBlock';
import { ForkMarker, ForkBranch } from './ForkMarker';
import { findForkPoints, buildChildrenByParent } from './childrenByParent';
import { collectMergedCampIds } from './campMergedState';
import type { PeerMetadata } from '../../types/council';
import type { PhaseEvent } from '../../hooks/usePersonaToolProgress';

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  loadingStatus?: string;
  /** Structured phase-event stream for the currently-loading turn. Each
   *  WS tool/peer/heartbeat transition contributes one record. Replaces
   *  the legacy string-derived `statusHistory` pipeline — individual peer
   *  terminal transitions no longer get swallowed by aggregate views. */
  phaseEvents?: PhaseEvent[];
  showTimestamps?: boolean;
  className?: string;
  currentModel?: string | null;
  sessionId?: string;
  personaId?: string;
  personaName?: string;
  /** Council Trail Task 6.7 — per-message peer-author metadata. Undefined
   *  for Solo Trail sessions; populated by useMessagesPeerMetadata in
   *  PersonaConversationSurface for council sessions. */
  peerMetadataByMessageId?: ReadonlyMap<string, PeerMetadata>;
  /** Council Trail Task 6.7 — active persona Set from useLens. Empty/null
   *  = canopy mode = all peers visible. */
  lensSnapshot?: import('../../utils/lensVisibility').LensSnapshot | null;
  /** ChatDAGRAGDAG sp3 — fork topology for the left-panel tree-view.
   *  Omitted → computed from `messages`. Zero forks → byte-identical flat render. */
  forkData?: { forks: string[]; childrenByParent: Map<string, string[]> };
  /** Lens-derived default expansion (canopy = expanded, single = collapsed). */
  forksExpandedByDefault?: boolean;
  /** Council surface only — selecting a branch drives the lens write-target.
   *  Absent (admin/work chat) → branch rows are display-only. */
  onFocusBranch?: (b: { personaId: string; turnId: string }) => void;
}

export const MessageList: React.FC<MessageListProps> = ({
  messages,
  isLoading = false,
  loadingStatus,
  phaseEvents,
  showTimestamps = true,
  className = '',
  currentModel,
  sessionId,
  personaId,
  personaName,
  peerMetadataByMessageId,
  lensSnapshot,
  forkData,
  forksExpandedByDefault,
  onFocusBranch,
}) => {
  const { darkMode } = useTheme();
  const loadingRef = useRef<HTMLDivElement>(null);
  const latestMessageRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(messages.length);
  const [statusExpanded, setStatusExpanded] = useState(true);
  // Tracks the id of the last assistant message we snapped to the top,
  // so a fresh assistant arrival only fires the pin-to-top once per
  // message (not on every unrelated state tick).
  const lastPinnedAsstIdRef = useRef<string | null>(null);
  // Tracks whether the user has actively scrolled away from the bottom.
  // Auto-scrolls are suppressed while this is true so we don't yank
  // someone who's reading history. Reset by a send or by a manual
  // scroll back down.
  const userScrolledAwayRef = useRef(false);

  // Re-expand on each new turn so the user sees the trail by default.
  useEffect(() => {
    if (isLoading) setStatusExpanded(true);
  }, [isLoading]);

  // Structured live trail comes from the hook's phaseEvents prop. Each
  // WS transition is already its own record (no aggregate-view collapse)
  // so this component no longer needs to parse / dedup strings. On page
  // reload, `phaseEvents` is empty and the persisted trail renders via
  // MessageBubble reading `metadata.phase_events` — one code path for
  // both live and rehydrated views.
  const liveEvents = phaseEvents ?? [];

  // Only auto-scroll if user is already near the bottom (not reading history)
  const isNearBottom = useCallback(() => {
    if (!containerRef.current) return true;
    const c = containerRef.current;
    return c.scrollHeight - c.scrollTop - c.clientHeight < 150;
  }, []);

  const scrollToBottom = useCallback(() => {
    if (!containerRef.current) return;
    const c = containerRef.current;
    c.scrollTo({ top: c.scrollHeight, behavior: 'smooth' });
  }, []);

  // Scroll the latest-message wrapper so its TOP aligns with the
  // viewport top. Used on turn-end: the new assistant bubble reads
  // from the top, and its PersistedPhaseTrail (which renders as the
  // first child of the wrapper, collapsed by default) stays visible
  // just above as a "phase bubble" summary the operator can expand
  // on demand.
  const scrollLatestToTop = useCallback(() => {
    const el = latestMessageRef.current;
    if (!el) return;
    el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, []);

  // Detect when the user manually scrolls away from the bottom. If
  // they do, auto-scrolls pause until they scroll back down. Sends
  // reset the state (see user-msg scroll below) because a brand-new
  // turn is a fresh attention target.
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    const onScroll = () => {
      const atBottom = c.scrollHeight - c.scrollTop - c.clientHeight < 80;
      userScrolledAwayRef.current = !atBottom;
    };
    c.addEventListener('scroll', onScroll, { passive: true });
    return () => c.removeEventListener('scroll', onScroll);
  }, []);

  // When loading starts (user sent a message), ALWAYS scroll to the
  // bottom — they just hit send and we want the pending-action
  // indicator in view. Also resets the user-scrolled-away flag
  // because a new turn starts with the user's attention at the end.
  useEffect(() => {
    if (isLoading) {
      userScrolledAwayRef.current = false;
      setTimeout(scrollToBottom, 50);
    }
  }, [isLoading, scrollToBottom]);

  // During a running turn, keep the phase trail's TAIL in view as new
  // events stream in. Uses scrollToBottom so if the trail outgrows
  // the viewport the most recent events stay visible (Alan's "always
  // show the bottom" ask). Gated by userScrolledAwayRef so we don't
  // yank someone who's reading history.
  const liveEventCount = phaseEvents?.length ?? 0;
  useEffect(() => {
    if (!isLoading) return;
    if (userScrolledAwayRef.current) return;
    const h = window.setTimeout(scrollToBottom, 30);
    return () => window.clearTimeout(h);
  }, [isLoading, liveEventCount, scrollToBottom]);

  // Turn-end pin: the moment a NEW assistant message lands, snap its
  // wrapper's top to the viewport top. The wrapper contains the
  // PersistedPhaseTrail (collapsed by default) followed by the
  // MessageBubble — so the user sees: [phase trail ▸ collapsed] at
  // the top, then the new answer reading cleanly down from there.
  //
  // Keyed on message id (not length) so we fire exactly once per
  // fresh arrival, even if isLoading / phaseEvents ticks ahead of
  // the messages update.
  useEffect(() => {
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') return;
    if (last.id === lastPinnedAsstIdRef.current) return;
    lastPinnedAsstIdRef.current = last.id;
    if (userScrolledAwayRef.current) return;
    // Two frames of delay so the DOM has the wrapper mounted AND the
    // PersistedPhaseTrail has committed its initial collapsed height;
    // scrollIntoView on a still-growing element lands off-target.
    const h = window.setTimeout(scrollLatestToTop, 120);
    return () => window.clearTimeout(h);
  }, [messages, scrollLatestToTop]);

  // Keep the count ref in sync for any legacy consumer (and for
  // parity with the prior pattern — other effects can lean on it).
  useEffect(() => {
    prevMessageCountRef.current = messages.length;
  }, [messages.length]);

  // Format model name for display
  const formatModelName = (modelId: string | null | undefined) => {
    if (!modelId) return null;
    const parts = modelId.split('/');
    return parts.length > 1 ? parts[1] : modelId;
  };

  const visibleMessages = messages.filter(m => m.role !== 'system');

  // ── ChatDAGRAGDAG sp3 — fork topology. Zero forks → flat render below
  //    (byte-identical to the pre-sp3 list, guarded by a snapshot test). ──
  const forks = forkData?.forks ?? findForkPoints(messages);
  const childrenByParent = forkData?.childrenByParent ?? buildChildrenByParent(messages);
  const hasForks = forks.length > 0;
  const forkSet = new Set(forks);
  // C2.2 — Camps folded back into the main trail (server truth); a fork whose
  // camp child is in this set shows a "↩ folded into main" marker (tree, not diamond).
  const mergedCampIds = collectMergedCampIds(messages);

  // Per-fork expansion overrides; the lens supplies the default.
  const [forkOverrides, setForkOverrides] = useState<Map<string, boolean>>(new Map());
  const isForkExpanded = (id: string) =>
    forkOverrides.has(id) ? forkOverrides.get(id)! : (forksExpandedByDefault ?? false);
  const toggleFork = (id: string) =>
    setForkOverrides(prev => {
      const next = new Map(prev);
      next.set(id, !isForkExpanded(id));
      return next;
    });

  // One row's markup — the SAME for the flat path and the tree path, so the
  // zero-fork render stays byte-identical to today.
  const renderRow = (message: Message, isLast: boolean) => (
    <React.Fragment key={message.id}>
      <div ref={isLast ? latestMessageRef : undefined}>
        <MessageRow
          message={message}
          showTimestamp={showTimestamps}
          modelName={
            message.role === 'assistant'
              ? formatModelName(
                  (message as any)?.model_used ??
                    (message as any)?.metadata?.model_used ??
                    (message as any)?.metadata?.model ??
                    currentModel
                )
              : undefined
          }
          sessionId={sessionId}
          personaId={personaId}
          personaName={personaName}
          peerMetadata={peerMetadataByMessageId?.get(message.id)}
          lensSnapshot={lensSnapshot}
        />
      </div>
    </React.Fragment>
  );

  // Tree render: designate peer-spine children (a Camp pin — metadata carries
  // persona_id / is_spine_root) as companion branches, pull their subtrees out
  // of the main flow, and surface each fork as an expandable marker. Falls back
  // to "first child stays on the main trail" when no child is a peer spine.
  const renderThread = (): React.ReactNode[] => {
    const byId = new Map(visibleMessages.map(m => [m.id, m]));
    const isPeerSpine = (id: string) => {
      const md = (byId.get(id)?.metadata ?? {}) as Record<string, any>;
      return !!(md.persona_id || md.is_spine_root);
    };
    const subtree = (rootId: string): string[] => {
      const out: string[] = [];
      const stack = [rootId];
      while (stack.length) {
        const id = stack.pop()!;
        out.push(id);
        for (const c of (childrenByParent.get(id) ?? [])) stack.push(c);
      }
      return out;
    };
    const branchRootsByFork = new Map<string, string[]>();
    const branchIds = new Set<string>();
    for (const f of forks) {
      const childIds = childrenByParent.get(f) ?? [];
      let roots = childIds.filter(isPeerSpine);
      if (roots.length === 0) roots = childIds.slice(1); // fallback: first child = main
      branchRootsByFork.set(f, roots);
      for (const r of roots) for (const id of subtree(r)) branchIds.add(id);
    }
    const mainMessages = visibleMessages.filter(m => !branchIds.has(m.id));

    const out: React.ReactNode[] = [];
    mainMessages.forEach((message, idx) => {
      out.push(renderRow(message, idx === mainMessages.length - 1));
      if (!forkSet.has(message.id)) return;
      const childIds = childrenByParent.get(message.id) ?? [];
      const roots = branchRootsByFork.get(message.id) ?? [];
      const branches: ForkBranch[] = roots.map(rootId => {
        const md = (byId.get(rootId)?.metadata ?? {}) as Record<string, any>;
        const joinerName =
          peerMetadataByMessageId?.get(rootId)?.displayName ??
          md.persona_name ?? md.persona_id ?? rootId;
        return { turnId: rootId, joinerName, task: md.task ?? '', personaId: md.persona_id ?? rootId,
                 kind: md.fork_id ? 'fork' : 'join', forkMode: md.fork_mode };
      });
      const expanded = isForkExpanded(message.id);
      // C2.2 — a folded-back companion subtree reads "↩ folded into main".
      // Tree stays [0]-only; this is a marker on the camp subtree, not a fan-in.
      const isFoldedBack = childIds.some((cid) => mergedCampIds.has(cid));
      out.push(
        <div key={`fork-${message.id}`} className="flex items-center">
          <ForkMarker
            count={childIds.length}
            branches={branches}
            expanded={expanded}
            onToggle={() => toggleFork(message.id)}
            onFocusBranch={onFocusBranch}
          />
          {isFoldedBack && (
            <span
              data-testid={`thread-folded-${message.id}`}
              className="ml-2 text-[10px] text-amber-600 dark:text-amber-300"
              title="This companion's work was folded back into the main trail"
            >
              ↩ folded into main
            </span>
          )}
        </div>
      );
      if (expanded) {
        for (const r of roots) {
          const rows = subtree(r)
            .map(id => byId.get(id))
            .filter((m): m is Message => !!m)
            .map(m => renderRow(m, false));
          out.push(
            <div key={`branch-${r}`} className="ml-4 border-l-2 border-gray-200 dark:border-gray-700 pl-2">
              {rows}
            </div>
          );
        }
      }
    });
    return out;
  };

  return (
    <div ref={containerRef} className={`flex-grow min-h-0 overflow-y-auto p-4 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} ${className}`}>
      {/* Persisted phase trail lives inside MessageBubble for every assistant
          message with `metadata.phase_events` — works for live-just-landed AND
          reloaded sessions, for every tool. sp3: ≥1 fork → tree-view; zero
          forks → byte-identical flat list (renderRow is the shared row). */}
      {hasForks
        ? renderThread()
        : visibleMessages.map((message, idx) =>
            renderRow(message, idx === visibleMessages.length - 1))}

      {/* Live loading indicator — shows between user message and pending
          response. PhaseTrailBlock renders directly from the hook's
          phaseEvents stream; every WS tool/peer/heartbeat transition is
          already its own record (no aggregate-view collapse). */}
      {isLoading && (
        <div ref={loadingRef}>
          <PhaseTrailBlock
            isLoading={true}
            loadingStatus={loadingStatus}
            events={liveEvents}
            expanded={statusExpanded}
            setExpanded={setStatusExpanded}
            darkMode={darkMode}
          />
        </div>
      )}
    </div>
  );
};
